// HR Integrations page — Slice 21 (Slack) + Slice 36 (Google Workspace).
// DESIGN §7.2.
//
// One card per provider. HR_Admin owns mutations; HR_Viewer sees the status
// read-only. MS Graph remains deferred (Slice 37+, see PROJECT.md §5.2).
//
// Dispatcher precedence is documented here so HR can predict where DMs go
// when both providers are connected: Slack wins. To route via Google,
// disconnect Slack.

import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Card } from '@/components/ui/Card.tsx';
import { useSession } from '@/lib/auth.ts';

interface SlackStatus {
  teamId: string;
  teamName: string;
  installedAt: string;
}

interface GoogleWorkspaceStatus {
  installerEmail: string;
  installerName: string;
  hostedDomain: string | null;
  installedAt: string;
}

interface IntegrationsResponse {
  slack: SlackStatus | null;
  googleWorkspace: GoogleWorkspaceStatus | null;
}

export function HrIntegrations() {
  const session = useSession('hr-shell');
  const canMutate = session.role === 'HR_Admin';
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();

  // Parse callback query params left by both OAuth callbacks. Clear after
  // 5s so a refresh doesn't keep the banner pinned.
  const slackSuccess = search.get('slack');
  const slackError = search.get('slack_error');
  const googleSuccess = search.get('google_workspace');
  const googleError = search.get('google_workspace_error');
  useEffect(() => {
    if (slackSuccess || slackError || googleSuccess || googleError) {
      const t = setTimeout(() => {
        const next = new URLSearchParams(search);
        next.delete('slack');
        next.delete('slack_error');
        next.delete('google_workspace');
        next.delete('google_workspace_error');
        setSearch(next, { replace: true });
      }, 5000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [slackSuccess, slackError, googleSuccess, googleError, search, setSearch]);

  const status = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get<IntegrationsResponse>('/api/hr/integrations'),
  });

  return (
    <section className="space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Workspace integrations</h1>
        <p className="text-sm text-muted-foreground">
          Connect besideU to your team's workspace tools so touch point reminders and HR safety
          alerts arrive where managers already work. Email delivery is the fallback when an
          integration isn't connected or temporarily fails. If both Slack and Google Workspace are
          connected, DMs go via Slack.
        </p>
      </header>

      {slackSuccess === 'connected' ? (
        <Banner tone="green">Slack connected successfully.</Banner>
      ) : null}
      {slackError ? (
        <Banner tone="red">
          Slack connection failed: <span className="font-mono text-xs">{slackError}</span>
        </Banner>
      ) : null}
      {googleSuccess === 'connected' ? (
        <Banner tone="green">Google Workspace connected successfully.</Banner>
      ) : null}
      {googleError ? (
        <Banner tone="red">
          Google Workspace connection failed:{' '}
          <span className="font-mono text-xs">{googleError}</span>
        </Banner>
      ) : null}

      <SlackCard
        status={status.data?.slack ?? null}
        loading={status.isPending}
        canMutate={canMutate}
        onChange={() => qc.invalidateQueries({ queryKey: ['integrations'] })}
      />
      <GoogleWorkspaceCard
        status={status.data?.googleWorkspace ?? null}
        loading={status.isPending}
        canMutate={canMutate}
        onChange={() => qc.invalidateQueries({ queryKey: ['integrations'] })}
      />
    </section>
  );
}

// =============================================================================
// Slack — Slice 21.
// =============================================================================

function SlackCard({
  status,
  loading,
  canMutate,
  onChange,
}: {
  status: SlackStatus | null;
  loading: boolean;
  canMutate: boolean;
  onChange: () => void;
}) {
  const install = useMutation({
    mutationFn: () => api.post<{ installUrl: string }>('/api/hr/integrations/slack/install', {}),
    onSuccess: ({ installUrl }) => {
      window.location.href = installUrl;
    },
  });
  const disconnect = useMutation({
    mutationFn: () => api.del<{ deleted: boolean }>('/api/hr/integrations/slack'),
    onSuccess: onChange,
  });
  const test = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; messageTs?: string }>('/api/hr/integrations/slack/test', {}),
  });

  return (
    <Card>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Slack</h2>
        <StatusPill
          loading={loading}
          connected={Boolean(status)}
          label={status ? `Connected · ${status.teamName}` : null}
        />
      </header>
      <p className="mt-2 text-sm text-muted-foreground">
        Touch point reminders go as DMs to the assigned Manager. The 7-day RTW unacknowledged alert
        goes as a DM to your named HR Safety Contact. Other notifications (red-flag safeguarding
        alerts) stay on email until further notice.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {!status ? (
          canMutate ? (
            <Button
              onClick={() => install.mutate()}
              loading={install.isPending}
              disabled={install.isPending}
            >
              Connect Slack
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">Only HR Admins can connect Slack.</p>
          )
        ) : (
          <>
            {canMutate ? (
              <Button
                variant="ghost"
                onClick={() => test.mutate()}
                loading={test.isPending}
                disabled={test.isPending}
              >
                Send a test DM
              </Button>
            ) : null}
            {canMutate ? (
              <Button
                variant="ghost"
                className="text-traffic-red hover:text-traffic-red"
                onClick={() => {
                  if (
                    confirm(
                      'Disconnect Slack? Future touch point reminders and HR safety alerts will go via Google Workspace (if connected) or email.',
                    )
                  ) {
                    disconnect.mutate();
                  }
                }}
                loading={disconnect.isPending}
                disabled={disconnect.isPending}
              >
                Disconnect
              </Button>
            ) : null}
          </>
        )}
      </div>

      {test.isSuccess ? (
        <p className="mt-3 text-sm text-traffic-green-text">Test DM sent — check your Slack.</p>
      ) : null}
      {test.error ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          {slackTestErrorMessage(test.error)}
        </p>
      ) : null}
      {install.error ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          Could not start the Slack install — please try again.
        </p>
      ) : null}
    </Card>
  );
}

// =============================================================================
// Google Workspace — Slice 36.
// =============================================================================

function GoogleWorkspaceCard({
  status,
  loading,
  canMutate,
  onChange,
}: {
  status: GoogleWorkspaceStatus | null;
  loading: boolean;
  canMutate: boolean;
  onChange: () => void;
}) {
  const install = useMutation({
    mutationFn: () =>
      api.post<{ installUrl: string }>('/api/hr/integrations/google-workspace/install', {}),
    onSuccess: ({ installUrl }) => {
      window.location.href = installUrl;
    },
  });
  const disconnect = useMutation({
    mutationFn: () => api.del<{ deleted: boolean }>('/api/hr/integrations/google-workspace'),
    onSuccess: onChange,
  });
  const test = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; messageName?: string }>(
        '/api/hr/integrations/google-workspace/test',
        {},
      ),
  });

  // The DM is sent as the consenting HR Admin's identity — surfaced in the
  // pill so an operator chasing a delivery question sees whose grant is in
  // play. If that user leaves, a different HR Admin re-installs to migrate.
  const connectedLabel = status
    ? `Connected as ${status.installerName}${status.hostedDomain ? ` · ${status.hostedDomain}` : ''}`
    : null;

  return (
    <Card>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Google Workspace</h2>
        <StatusPill loading={loading} connected={Boolean(status)} label={connectedLabel} />
      </header>
      <p className="mt-2 text-sm text-muted-foreground">
        Touch point reminders and the 7-day RTW unacknowledged alert go as Google Chat DMs. Messages
        are sent under the identity of the HR Admin who connected the integration — if that person
        leaves the org, a different HR Admin should re-install to migrate. Slack delivery (when both
        are connected) takes precedence.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {!status ? (
          canMutate ? (
            <Button
              onClick={() => install.mutate()}
              loading={install.isPending}
              disabled={install.isPending}
            >
              Connect Google Workspace
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Only HR Admins can connect Google Workspace.
            </p>
          )
        ) : (
          <>
            {canMutate ? (
              <Button
                variant="ghost"
                onClick={() => test.mutate()}
                loading={test.isPending}
                disabled={test.isPending}
              >
                Send a test DM
              </Button>
            ) : null}
            {canMutate ? (
              <Button
                variant="ghost"
                className="text-traffic-red hover:text-traffic-red"
                onClick={() => {
                  if (
                    confirm(
                      'Disconnect Google Workspace? Future touch point reminders and HR safety alerts will go via Slack (if connected) or email.',
                    )
                  ) {
                    disconnect.mutate();
                  }
                }}
                loading={disconnect.isPending}
                disabled={disconnect.isPending}
              >
                Disconnect
              </Button>
            ) : null}
          </>
        )}
      </div>

      {test.isSuccess ? (
        <p className="mt-3 text-sm text-traffic-green-text">
          Test DM sent — check your Google Chat.
        </p>
      ) : null}
      {test.error ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          {googleTestErrorMessage(test.error)}
        </p>
      ) : null}
      {install.error ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          Could not start the Google Workspace install — please try again.
        </p>
      ) : null}
    </Card>
  );
}

// =============================================================================
// Shared bits
// =============================================================================

function Banner({ tone, children }: { tone: 'green' | 'red'; children: React.ReactNode }) {
  const cls =
    tone === 'green'
      ? 'border-traffic-green/30 bg-traffic-green/10 text-traffic-green-text'
      : 'border-traffic-red/40 bg-traffic-red/10 text-traffic-red';
  return <div className={`rounded-md border px-3 py-2 text-sm ${cls}`}>{children}</div>;
}

function StatusPill({
  connected,
  loading,
  label,
}: {
  connected: boolean;
  loading: boolean;
  label: string | null;
}) {
  if (loading) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Checking…
      </span>
    );
  }
  if (!connected) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Not connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-traffic-green/20 px-2 py-0.5 text-xs text-traffic-green-text">
      {label ?? 'Connected'}
    </span>
  );
}

function slackTestErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'Slack is not connected — connect first.';
    if (err.status === 400) {
      const code = (err.body as { error?: unknown } | null)?.error;
      if (code === 'user_not_found') {
        return "Your besideU email isn't on this Slack workspace, so DMs won't reach you. Either add the email to Slack or notifications will continue arriving by email.";
      }
    }
    if (err.status === 502)
      return "Slack didn't accept the message — try again, or check whether the bot was removed.";
  }
  return 'Test DM failed. Please try again.';
}

function googleTestErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'Google Workspace is not connected — connect first.';
    if (err.status === 400) {
      const code = (err.body as { error?: unknown } | null)?.error;
      if (code === 'user_not_found') {
        return "Your besideU email isn't a Google Workspace user, so the DM can't reach you. Notifications will continue arriving by email until that's resolved.";
      }
    }
    if (err.status === 502)
      return "Google didn't accept the message — try again, or check whether the workspace admin revoked the grant.";
  }
  return 'Test DM failed. Please try again.';
}
